import axios from 'axios'

import { refreshLeafletDate } from '@/api/leaflets'
import { fetchLeafletsXmlOrdering } from '@/api/repositories/leaflets-order/leafletsOrderRepository'
import { updateLeaflet } from '@/api/repositories/leafletsRepository'
import { capitalizeFirstLetter } from '@/utils'

// sort helpers
const platformPriority = platform => `${platform.toLowerCase()}Priority`

// Helper function to find leaflet with same priority
const findLeafletWithSamePriority = (leaflets, currentLeafletId, priorityKey, priorityValue) => {
  return leaflets
    .filter(leaflet => leaflet.id !== currentLeafletId)
    .find(leaflet => leaflet[priorityKey] === priorityValue)
}

// Helper function to update leaflet and commit changes
const updateAndCommitLeaflet = async (leaflet, storeId, commit, dispatch) => {
  const response = await updateLeaflet(leaflet, storeId)

  if (response.status === 204) {
    commit('UPDATE_LEAFLET', leaflet)
    dispatch('sortLeaflets')
    return true
  }

  return false
}

const sortByDate = (a, b) => {
  return a.createdAt > b.createdAt ? -1 : 1
}
const sortByActivateDate = (a, b) => {
  return a.activateReadyStatusAt > b.activateReadyStatusAt ? -1 : 1
}
const sortByPriority = prioType => (a, b) => {
  return a[prioType] - b[prioType]
}
const sortByAnyExistingPriority = (leaflets, platforms) => {
  const withAnyPriority = []
  const withoutAnyPriority = []

  leaflets.forEach(l => {
    if (platforms.reduce((acc, platform) => acc || l[platformPriority(platform)], false)) {
      withAnyPriority.push(l)
    } else {
      withoutAnyPriority.push(l)
    }
  })

  const sortedAscWithPriorities = withAnyPriority.sort((a, b) => {
    const AMinPriority = Math.min(
      ...platforms.map(platform => a[platformPriority(platform)]).filter(priority => priority > 0)
    )
    const BMinPriority = Math.min(
      ...platforms.map(platform => b[platformPriority(platform)]).filter(priority => priority > 0)
    )

    return AMinPriority - BMinPriority
  })
  return [...sortedAscWithPriorities, ...withoutAnyPriority.sort(sortByDate).sort(sortByActivateDate)]
}
// sort: match array position number with priorities. Can not use .sort function as it does not provide index of elements.
const sortBasedOnPriorities = (withPriority, withoutPriority, prioType) => {
  const leaflets = []
  const leafletsNumber = withPriority.length + withoutPriority.length

  for (let i = 1; i <= leafletsNumber; i++) {
    if (withPriority.length && (withPriority[0][`${prioType.toLowerCase()}Priority`] <= i || !withoutPriority.length)) {
      leaflets.push(withPriority[0])
      withPriority.shift()
    } else {
      leaflets.push(withoutPriority[0])
      withoutPriority.shift()
    }
  }

  return leaflets
}

const leafletsOrder = {
  state: {
    isLoading: false,
    allLeaflets: [],
    leafletsToDisplay: [],
    requestToken: null,
    activePlatform: 'all'
  },

  mutations: {
    SET_DATA_LOADER: (state, loading) => {
      state.isLoading = loading
    },
    SET_LEAFLETS: (state, allLeaflets) => {
      state.allLeaflets = allLeaflets
    },
    SET_LEAFLETS_TO_DISPLAY: (state, leaflets) => {
      state.leafletsToDisplay = leaflets
    },
    SET_CANCEL_REQUEST_TOKEN: (state, token) => {
      state.requestToken = token
    },
    CHANGE_CURRENT_PLAFTORM: (state, newPlatform) => {
      state.activePlatform = newPlatform
    },
    UPDATE_LEAFLET: (state, leafletToUpdate) => {
      state.allLeaflets = state.allLeaflets.map(leaflet => {
        if (leaflet.id === leafletToUpdate.id) {
          return leafletToUpdate
        }
        return leaflet
      })

      state.leafletsToDisplay = state.leafletsToDisplay.map(leaflet => {
        if (leaflet.id === leafletToUpdate.id) {
          return leafletToUpdate
        }
        return leaflet
      })
    }
  },

  actions: {
    async getLeaflets({ commit, dispatch, state }) {
      const cancelToken = axios.CancelToken.source()
      // abort previous pending request
      if (state.requestToken) {
        state.requestToken.cancel('request canceled')
        commit('SET_CANCEL_REQUEST_TOKEN', null)
      }

      commit('SET_DATA_LOADER', true)
      commit('SET_CANCEL_REQUEST_TOKEN', cancelToken)
      try {
        const data = await fetchLeafletsXmlOrdering(cancelToken)

        commit('SET_LEAFLETS', data)
        dispatch('sortLeaflets')
        commit('SET_DATA_LOADER', false)
        commit('SET_CANCEL_REQUEST_TOKEN', null)
      } catch (err) {
        commit('SET_DATA_LOADER', false)
        throw new Error('Request canceled')
      }
    },
    async refreshLeafletActivationDate({ commit, dispatch, state }, leafletId) {
      commit('SET_DATA_LOADER', true)
      const { status } = await refreshLeafletDate(leafletId)

      if (status === 204) {
        const leafletToUpdate = state.allLeaflets.find(leaflet => leaflet.id === leafletId)
        const updatedLeaflet = {
          ...leafletToUpdate,
          activateReadyStatusAt: new Date().toISOString()
        }

        commit('UPDATE_LEAFLET', updatedLeaflet)
        dispatch('sortLeaflets')
        commit('SET_DATA_LOADER', false)

        return updatedLeaflet
      }

      return null
    },
    changePlatform({ commit, dispatch }, newPlatform) {
      commit('CHANGE_CURRENT_PLAFTORM', newPlatform)
      dispatch('sortLeaflets')
    },
    sortLeaflets({ state, rootState, commit }) {
      if (state.activePlatform === 'all') {
        commit(
          'SET_LEAFLETS_TO_DISPLAY',
          sortByAnyExistingPriority(state.allLeaflets, rootState.app.platforms.leafletPlatforms)
        )
      } else {
        const isVisiblePlatformKey = `isVisibleOn${capitalizeFirstLetter(state.activePlatform)}`
        const withPriority = state.allLeaflets
          .filter(leaflet => leaflet[isVisiblePlatformKey] && leaflet[`${platformPriority(state.activePlatform)}`])
          .sort(sortByPriority(platformPriority(state.activePlatform)))
        const withoutPriority = state.allLeaflets
          .filter(leaflet => leaflet[isVisiblePlatformKey] && !leaflet[`${platformPriority(state.activePlatform)}`])
          .sort(sortByDate)
          .sort(sortByActivateDate)

        commit('SET_LEAFLETS_TO_DISPLAY', sortBasedOnPriorities(withPriority, withoutPriority, state.activePlatform))
      }

      return true
    },
    async updateLeafletPlatformPriority({ state, commit, dispatch }, leaflet) {
      if (state.activePlatform === 'all') {
        return updateAndCommitLeaflet(leaflet, leaflet.storeId, commit, dispatch)
      }

      const priorityKey = platformPriority(state.activePlatform)
      const currentPriority = leaflet[priorityKey]
      const hasPriority = currentPriority > 0

      if (!hasPriority) {
        const leafletWithNullPriority = { ...leaflet, [priorityKey]: null }
        return updateAndCommitLeaflet(leafletWithNullPriority, leaflet.storeId, commit, dispatch)
      }

      const conflictingLeaflet = findLeafletWithSamePriority(
        state.leafletsToDisplay,
        leaflet.id,
        priorityKey,
        currentPriority
      )

      if (conflictingLeaflet) {
        return { action: 'overwrite', leafletWithSamePriority: conflictingLeaflet }
      }

      return updateAndCommitLeaflet(leaflet, leaflet.storeId, commit, dispatch)
    },
    async updateAllLeafletPlatformsPriority({ rootState, state, commit, dispatch }, leaflet) {
      const leafletPriorities = rootState.app.platforms.leafletPlatforms.map(platform => platformPriority(platform))

      const leafletsWithSamePriority = []

      if (leaflet.allPriority) {
        state.allLeaflets.forEach(l => {
          leafletPriorities.forEach(priority => {
            if (l[priority] === leaflet.allPriority) {
              leafletsWithSamePriority.push(l)
            }
          })
        })
      }

      if (leafletsWithSamePriority.length) {
        return { action: 'overwrite', leafletsWithSamePriority }
      } else {
        leafletPriorities.map(priority => {
          leaflet[priority] = leaflet.allPriority
        })

        delete leaflet.allPriority

        const response = await updateLeaflet(leaflet, leaflet.storeId)

        if (response.status === 204) {
          commit('UPDATE_LEAFLET', leaflet)
          dispatch('sortLeaflets')
          return true
        }

        return false
      }
    }
  }
}

export default leafletsOrder
